import "./maplibre-worker";

import { useMemo, type ReactNode, type Ref } from "react";
import Map, { type MapProps, type MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import { useTheme } from "@/lib/theme";
import { cardBase } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { PORTFOLIO_OVERVIEW_VIEW } from "@/lib/map-camera";

/** Carto OSM-based vector styles — no proprietary key; dark/light aligned to theme (ADR-018). */
const TILE_STYLE = {
  light: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
  dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
} as const;

export type GeoMapProps = {
  className?: string;
  /** Inner map area height class; defaults to a tall Etusivu-friendly panel. */
  mapClassName?: string;
  /**
   * When true, skip the Tier-1 `cardBase` chrome so a parent (e.g. InventoryMap header)
   * can own the panel shell. Default false — standalone GeoMap uses `cardBase`.
   */
  bare?: boolean;
  children?: ReactNode;
  initialViewState?: MapProps["initialViewState"];
  onLoad?: MapProps["onLoad"];
  onClick?: MapProps["onClick"];
  onMouseMove?: MapProps["onMouseMove"];
  onMouseLeave?: MapProps["onMouseLeave"];
  cursor?: MapProps["cursor"];
  interactiveLayerIds?: MapProps["interactiveLayerIds"];
  mapRef?: Ref<MapRef>;
};

/**
 * Reusable MapLibre shell — pan/zoom, theme-aware OSM tiles, Tier-1 `cardBase` container.
 * Cluster / marker layers are composed as children (see `BuildingClusterMap`, used by
 * `InventoryMap` and `OverviewMap` — the same world-map shell).
 */
export function GeoMap({
  className,
  mapClassName,
  bare = false,
  children,
  initialViewState,
  onLoad,
  onClick,
  onMouseMove,
  onMouseLeave,
  cursor,
  interactiveLayerIds,
  mapRef,
}: GeoMapProps) {
  const { mode } = useTheme();
  const mapStyle = useMemo(
    () => (mode === "dark" ? TILE_STYLE.dark : TILE_STYLE.light),
    [mode],
  );

  return (
    <div
      className={cn(
        bare ? "overflow-hidden" : cn(cardBase, "overflow-hidden"),
        "relative",
        className,
      )}
    >
      {/* min-height gives the panel a used size, but `height: 100%` on MapLibre does not
          resolve against min-height (CSS percentage height needs a specified height). An
          absolute fill uses the padding box instead — otherwise `.maplibregl-map` stays
          0px tall and the inventory map is a blank rectangle. */}
      <div className={cn("relative w-full min-h-[420px]", mapClassName)}>
        <div className="absolute inset-0">
          <Map
            ref={mapRef}
            mapStyle={mapStyle}
            initialViewState={initialViewState ?? PORTFOLIO_OVERVIEW_VIEW}
            style={{ width: "100%", height: "100%" }}
            attributionControl={false}
            cursor={cursor}
            onLoad={onLoad}
            onClick={onClick}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            interactiveLayerIds={interactiveLayerIds}
          >
            {children}
          </Map>
          {/* OSM / Carto ToS require a credit (ADR-018). The MapLibre (i) control is
              hidden; this line is the remaining, non-interactive attribution. */}
          <p className="absolute bottom-1.5 right-2 m-0 text-[9px] leading-none text-muted-foreground/50 pointer-events-none">
            © OpenStreetMap · CARTO
          </p>
        </div>
      </div>
    </div>
  );
}
